import {
  Component,
  ElementRef,
  inject,
  AfterViewInit,
  signal,
  computed,
  effect,
  viewChild,
  ChangeDetectionStrategy,
} from '@angular/core';
import { Router, RouterOutlet } from '@angular/router';
import {
  PaHeaderComponent,
  PaFooterComponent,
  AuthService,
  LoaderService,
} from '@pa-angular/pa-fe-boilerplate';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatDialog } from '@angular/material/dialog';
import { AppConfigService } from './root-services/app-config.service';
import { EnvironmentService } from './root-services/environment.service';
import { LOGIN_ROUTE, SUBSCRIPTION_SELECTOR_ROUTE } from './app.const';
import { BreadcrumbComponent } from './shared/components/breadcrumb/breadcrumb.component';
import { FeedbackButtonComponent } from './shared/components/feedback-button/feedback-button.component';
import { CookieConsentBannerComponent } from './shared/components/cookie-consent-banner/cookie-consent-banner.component';
import { WorkspaceSidebarComponent } from './features/workspace-sidebar/workspace-sidebar.component';
import { WorkspaceSidebarStateService } from './features/workspace-sidebar/workspace-sidebar-state.service';
import { ViewportService } from './root-services/viewport.service';
import { NavigationContextService } from './root-services/navigation-context.service';
import {
  CookiePreferencesDialogComponent,
  COOKIE_PREFERENCES_DIALOG_CONFIG,
} from './shared/components/cookie-preferences-dialog/cookie-preferences-dialog.component';
import { AppScrollService } from './root-services/app-scroll.service';

/**
 * AppComponent
 *
 * Root component for the application
 * Integrates enterprise header and footer with authentication
 * Provides consistent layout across all routes
 *
 * Enterprise Layout Structure:
 * - PaHeaderComponent: Enterprise header with logo, navigation, user dropdown
 * - BreadcrumbComponent: Global breadcrumb navigation as second header
 * - WorkspaceSidebarComponent: Global workspace sidebar navigation (Story 2.8)
 * - router-outlet: Dynamic content area for route components
 * - CookieConsentBannerComponent: GDPR-compliant consent banner (Story 6.2)
 * - PaFooterComponent: Enterprise footer with legal links
 *
 * Authentication Integration:
 * - Header displays user information when authenticated
 * - Logout functionality clears auth state and redirects to login
 * - Workspace navigation redirects to workspace launcher (/workspaces)
 * - Cookie preferences allows users to manage consent settings (Story 6.7)
 */
@Component({
  selector: 'app-root',
  standalone: true,
  imports: [
    RouterOutlet,
    PaHeaderComponent,
    PaFooterComponent,
    BreadcrumbComponent,
    MatProgressBarModule,
    FeedbackButtonComponent,
    CookieConsentBannerComponent,
    WorkspaceSidebarComponent,
  ],
  templateUrl: './app.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './app.component.scss',
})
export class AppComponent implements AfterViewInit {
  configService = inject(AppConfigService);
  authService = inject(AuthService);
  loaderService = inject(LoaderService);
  private readonly router = inject(Router);
  private readonly envService = inject(EnvironmentService);
  private readonly dialog = inject(MatDialog);
  private readonly sidebarStateService = inject(WorkspaceSidebarStateService);
  private readonly viewportService = inject(ViewportService);
  private readonly navContextService = inject(NavigationContextService);
  private readonly appScrollService = inject(AppScrollService);

  private readonly appContentRef =
    viewChild<ElementRef<HTMLElement>>('appContent');

  loginRouteName = LOGIN_ROUTE;

  /** Mobile viewport detection signal - use ViewportService */
  private readonly isMobile = computed(() => this.viewportService.isMobile());

  /**
   * Signal tracking whether sidebar should be visible on current route
   * Story 2.8: Prevents margin spacing on workspaces launcher page
   */
  showSidebarSpacing = signal<boolean>(true);

  /**
   * Computed sidebar margin based on actual sidebar width
   * Updates dynamically when sidebar is collapsed/expanded
   * On mobile: Always returns '0px' to allow sidebar to overlay content
   * On desktop: Returns sidebar width to push content
   */
  sidebarMargin = computed(() => {
    // On mobile, sidebar always overlays (no margin adjustment)
    if (this.isMobile()) {
      return '0px';
    }

    if (!this.showSidebarSpacing()) {
      return '0px';
    }
    return `${this.sidebarStateService.sidebarWidth()}px`;
  });

  constructor() {
    // Drive showSidebarSpacing from NavigationContextService (reactive, no URL parsing)
    effect(() => {
      this.showSidebarSpacing.set(!this.navContextService.isSystemContext());
    });
  }

  /**
   * Measure header/footer render performance after view initialization
   */
  ngAfterViewInit(): void {
    this.appScrollService.setAppContentElement(
      this.appContentRef()?.nativeElement ?? null,
    );

    // Performance mark: Header and footer rendered
    performance.mark('header-footer-render');

    // Measure time from environment init to header/footer render
    const envInitMark = performance.getEntriesByName('env-init-end', 'mark')[0];
    if (envInitMark) {
      performance.measure(
        'header-footer-render-time',
        'env-init-end',
        'header-footer-render',
      );

      // Log performance measurement (development mode)
      const env = this.envService.env();
      if (env?.local) {
        const renderMeasure = performance.getEntriesByName(
          'header-footer-render-time',
          'measure',
        )[0];
        console.log(
          `[Performance] Header/Footer render: ${renderMeasure?.duration.toFixed(2)}ms after auth state`,
        );
      }
    }
  }

  /**
   * Handle logout event from header
   * Clears authentication state and redirects to login
   */
  onLogout(): void {
    this.authService.logout(this.loginRouteName);
  }

  /**
   * Handle workspace click event from header
   * Navigates to workspace launcher (/workspaces route)
   */
  onWorkspaceClick(): void {
    this.router.navigate([SUBSCRIPTION_SELECTOR_ROUTE]);
  }

  /**
   * Handle cookie preferences click event from header
   * Opens cookie preferences dialog
   * Story 6.7: Cookie Preferences Management
   */
  onCookiePreferencesClick(): void {
    this.dialog.open(
      CookiePreferencesDialogComponent,
      COOKIE_PREFERENCES_DIALOG_CONFIG,
    );
  }
}
